import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value < min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    this.#syncMinusButtonIcon(value, min);
  }

  /**
   * Cart quantity of 1 should behave like remove, not disabled decrement.
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { min, value } = this.getCurrentValues();
    if (value <= min) {
      this.refs.quantityInput.value = '0';
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    this.updateQuantity(-1);
  }

  /**
   * Swaps the minus icon to delete when cart quantity is at its minimum.
   * Only applies inside the cart drawer.
   * @param {number} value
   * @param {number} min
   */
  #syncMinusButtonIcon(value, min) {
    if (!this.closest('.cart-drawer')) return;

    const minusIcon = this.refs.minusButton.querySelector('.icon-plus');
    const deleteIcon = this.refs.minusButton.querySelector('.quantity-delete-icon');
    const shouldShowDelete = value <= min;

    minusIcon?.classList.toggle('hidden', shouldShowDelete);
    deleteIcon?.classList.toggle('hidden', !shouldShowDelete);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
